import { api } from "@/shared/api";

/**
 * 产品注册表(product registry)服务。
 *
 * 后端政策(2026-06-05 调整):
 * - **读**(`list`)走租户端 `GET /tenant/products`,租户管理员 / 经理可见自己的产品线;
 *   代客上下文也满足(会被追加 ROLE_TENANT_ADMIN + 有效租户=act_as)。
 * - **写**(`upsert` / `delete`)仍只在平台超管代客登录下可用(后端 `@sec.isImpersonating()`
 *   二次校验,保语料质量)。
 *
 * 契约尚未进 api-spec.yaml(本地手抄 DTO),后续走 proposal 对齐。
 */

export type ProductNodeType = "category" | "group" | "product";

// 后端 status 是整型:1=上架/启用,0=下架/归档。
export type ProductNodeStatus = 0 | 1;

/**
 * 对客可见性(D 批 codex 复审 #3;后端 V63 逐节点列,默认 public)。
 *
 * ai 的 sync-facts 按节点实际值给事实块打 L0 可见性轴:internal 节点(attributes 里常放成本价 /
 * 内部规格)的事实不进公开客服 bot 与官网的检索面。**与 status 正交** —— status=0 是下架(事实整块
 * 撤回),internal 是"在售但只给内部看"。
 */
export type ProductNodeVisibility = "public" | "internal";

// local-only: backend schema gap — GET /platform/products → { nodes: [...] }
export interface ProductNode {
  code: string;
  type: ProductNodeType;
  parent: string | null;
  level: number;
  name: string;
  aliases: string[];
  summary: string | null;
  sellingPoints: string[];
  attributes?: Record<string, unknown> | null;
  status: ProductNodeStatus;
  sortWeight: number;
  visibility?: ProductNodeVisibility; // 老数据/老后端可能缺 → 消费侧按 public 兜底
  /**
   * 产品预置图的 **object key** 列表(波3-①,2026-07-26)。
   *
   * 后端 `xb_product_node.sample_images` 这一列从 D2 起就有、`/internal/products/resolve` 也一直在
   * 给 ai 下发 —— 但**此前前端类型里没有这个字段,所以 upsert 永远不发**;加上 KB 反向桥的抽取端
   * 从不产出这个键、产品树也没有传图 UI,实测结果是这一列**零写入者**。补上前端这一环,树才可能非空。
   *
   * ★存的是 key 不是 URL:URL 会过期。要显示时走 `presignImage` 现签(海报线 2026-07-09
   * 生产 i2i 事故之后的全仓口径)。
   */
  sampleImages?: string[];
}

// local-only: backend schema gap — POST /platform/products body
export interface ProductNodeUpsertRequest {
  nodeCode: string;
  nodeType: ProductNodeType;
  parentCode: string | null;
  name: string;
  aliases: string[];
  summary?: string;
  sellingPoints: string[];
  attributes?: Record<string, unknown>;
  sortWeight: number;
  status: ProductNodeStatus;
  /** 不传 → 后端沿用既有值(新建则 public);传了才改。 */
  visibility?: ProductNodeVisibility;
  /** 产品预置图 object key 列表(波3-①)。先 `uploadImage` 拿 key,再随节点一起保存。 */
  sampleImages?: string[];
}

export const productService = {
  // 读:租户端只读端点,返回 ApiResponse{data: ProductNode[]};api.get 解包后即数组。
  // admin/manager 真实登录与平台代客两种上下文都可用。
  list: () => api.get<ProductNode[]>("/tenant/products"),
  // 写:仍走平台代客端点(后端 @sec.isImpersonating())。
  upsert: (req: ProductNodeUpsertRequest) => api.post<ProductNode>("/platform/products", req),
  delete: (nodeCode: string) => api.delete<void>(`/platform/products/${nodeCode}`),

  /**
   * 产品图上传(波3-①)→ 返回稳定 key,由调用方写进 `sampleImages` 再 upsert。
   *
   * 分两步(传图 / 存节点)是刻意的:传了图但没点保存,不该在树上留下半条数据。
   * 门与 upsert 相同(平台代客态)—— 产品树本来就只有这一条写入路径,图不该另开一道宽门。
   */
  uploadImage: (nodeCode: string, file: File): Promise<{ key: string; url: string }> => {
    const form = new FormData();
    form.append("file", file);
    return api.post<{ key: string; url: string }>(`/platform/products/${nodeCode}/images`, form);
  },

  /**
   * 产品图预览重签:落库存的是永久 key,每次要显示时现签。
   *
   * ★走**租户端**只读端点(T1-② 走查抓的缺陷,2026-07-26):此前打 `/platform/.../presign`
   * (平台代客门)→ 租户角色在创建页「从产品库选」里签缩略图全部 403,库列表永远占位图。
   * 与 `list` 同一口径:租户真实登录与平台代客两种上下文都可用(代客 JWT 落 TENANT_ADMIN)。
   */
  presignImage: (key: string) =>
    api.get<{ key: string; url: string }>(
      `/tenant/products/images/presign?key=${encodeURIComponent(key)}`
    ),
};
